// WholesalePOs.js — outbound wholesale orders (buyer → brand)
//
// Wholesale orders are tracking/workflow only. Units committed to wholesale are
// removed from Shopify's available count UP FRONT, so the daily Shopify sync
// already reflects the reduction in inventory_on_hand. Therefore these orders
// must NOT write inventory_transactions on ship — doing so would double-deduct.
// The per-line shopify_already_deducted flag (default true) is a safety net for
// the rare line that was NOT pre-pulled from Shopify; only those feed the
// "Allocated to Wholesale" figure on the Inventory page.
//
// Run this SQL in Supabase before using this page:
//
// create table wholesale_customers (
//   id uuid primary key default gen_random_uuid(),
//   brand_id uuid references brands(id) not null,
//   name text not null,
//   contact_name text,
//   contact_email text,
//   payment_terms text,
//   notes text,
//   created_at timestamptz default now()
// );
// alter table wholesale_customers enable row level security;
// create policy "brand_access" on wholesale_customers
//   using (brand_id in (select brand_id from user_brand_access where user_id = auth.uid())
//     or exists (select 1 from user_profiles where id = auth.uid() and role = 'super_admin'));
//
// create table wholesale_orders (
//   id uuid primary key default gen_random_uuid(),
//   brand_id uuid references brands(id) not null,
//   customer_id uuid references wholesale_customers(id),
//   order_number text not null,
//   ship_date date,
//   status text not null default 'draft',  -- draft | confirmed | shipped | closed | cancelled
//   notes text,
//   created_at timestamptz default now(),
//   updated_at timestamptz default now()
// );
// alter table wholesale_orders enable row level security;
// create policy "brand_access" on wholesale_orders
//   using (brand_id in (select brand_id from user_brand_access where user_id = auth.uid())
//     or exists (select 1 from user_profiles where id = auth.uid() and role = 'super_admin'));
//
// create table wholesale_order_lines (
//   id uuid primary key default gen_random_uuid(),
//   wholesale_order_id uuid references wholesale_orders(id) on delete cascade not null,
//   product_id uuid references products(id),
//   quantity int not null,
//   unit_price numeric,
//   shopify_already_deducted boolean default true,
//   fulfilled_quantity int default 0,
//   notes text
// );
// alter table wholesale_order_lines enable row level security;
// create policy "brand_access" on wholesale_order_lines
//   using (wholesale_order_id in (
//     select id from wholesale_orders where brand_id in (
//       select brand_id from user_brand_access where user_id = auth.uid()
//     )
//   ) or exists (select 1 from user_profiles where id = auth.uid() and role = 'super_admin'));

import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Plus, Search, X, Store, Trash2, AlertTriangle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'

const EMPTY = { order_number: '', customer_id: '', ship_date: '', status: 'draft', notes: '' }
const STATUS_BADGE = { draft: 'neutral', confirmed: 'accent', shipped: 'info', closed: 'success', cancelled: 'danger' }

const SETUP_SQL = `create table wholesale_customers (
  id uuid primary key default gen_random_uuid(),
  brand_id uuid references brands(id) not null,
  name text not null,
  contact_name text,
  contact_email text,
  payment_terms text,
  notes text,
  created_at timestamptz default now()
);
alter table wholesale_customers enable row level security;
create policy "brand_access" on wholesale_customers
  using (
    brand_id in (select brand_id from user_brand_access where user_id = auth.uid())
    or exists (select 1 from user_profiles where id = auth.uid() and role = 'super_admin')
  );

create table wholesale_orders (
  id uuid primary key default gen_random_uuid(),
  brand_id uuid references brands(id) not null,
  customer_id uuid references wholesale_customers(id),
  order_number text not null,
  ship_date date,
  status text not null default 'draft',
  notes text,
  created_at timestamptz default now(),
  updated_at timestamptz default now()
);
alter table wholesale_orders enable row level security;
create policy "brand_access" on wholesale_orders
  using (
    brand_id in (select brand_id from user_brand_access where user_id = auth.uid())
    or exists (select 1 from user_profiles where id = auth.uid() and role = 'super_admin')
  );

create table wholesale_order_lines (
  id uuid primary key default gen_random_uuid(),
  wholesale_order_id uuid references wholesale_orders(id) on delete cascade not null,
  product_id uuid references products(id),
  quantity int not null,
  unit_price numeric,
  shopify_already_deducted boolean default true,
  fulfilled_quantity int default 0,
  notes text
);
alter table wholesale_order_lines enable row level security;
create policy "brand_access" on wholesale_order_lines
  using (
    wholesale_order_id in (
      select id from wholesale_orders where brand_id in (
        select brand_id from user_brand_access where user_id = auth.uid()
      )
    )
    or exists (select 1 from user_profiles where id = auth.uid() and role = 'super_admin')
  );`

export default function WholesalePOs() {
  const { activeBrand } = useAuth()
  const [orders, setOrders] = useState([])
  const [customers, setCustomers] = useState([])
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('active')
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [newBuyer, setNewBuyer] = useState(false)
  const [buyerName, setBuyerName] = useState('')
  const [buyerEmail, setBuyerEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [formError, setFormError] = useState('')
  const [tableError, setTableError] = useState(false)

  useEffect(() => { if (activeBrand) load() }, [activeBrand])

  async function load() {
    setLoading(true)
    const { data: orderData, error: orderErr } = await supabase
      .from('wholesale_orders')
      .select('*, wholesale_customers(name), wholesale_order_lines(quantity)')
      .eq('brand_id', activeBrand.id)
      .order('created_at', { ascending: false })
    if (orderErr && (orderErr.code === '42P01' || orderErr.message?.includes('does not exist'))) {
      setTableError(true); setLoading(false); return
    }
    const { data: custData } = await supabase
      .from('wholesale_customers').select('id, name').eq('brand_id', activeBrand.id).order('name')
    setOrders(orderData || [])
    setCustomers(custData || [])
    setTableError(false)
    setLoading(false)
  }

  async function checkDuplicate(orderNumber) {
    const { data } = await supabase.from('wholesale_orders').select('id')
      .eq('brand_id', activeBrand.id).eq('order_number', orderNumber)
    return data && data.length > 0
  }

  function openNew() {
    setForm(EMPTY); setNewBuyer(false); setBuyerName(''); setBuyerEmail(''); setFormError(''); setModal(true)
  }

  async function save() {
    setFormError(''); setSaving(true)
    if (await checkDuplicate(form.order_number)) {
      setFormError('An order with this number already exists.'); setSaving(false); return
    }
    let customerId = form.customer_id || null
    if (newBuyer) {
      if (!buyerName.trim()) { setFormError('Enter a buyer name.'); setSaving(false); return }
      const { data: cust, error: custErr } = await supabase.from('wholesale_customers')
        .insert({ brand_id: activeBrand.id, name: buyerName.trim(), contact_email: buyerEmail.trim() || null })
        .select('id').single()
      if (custErr) { setFormError('Could not create buyer: ' + custErr.message); setSaving(false); return }
      customerId = cust.id
    }
    const payload = {
      brand_id: activeBrand.id,
      customer_id: customerId,
      order_number: form.order_number,
      ship_date: form.ship_date || null,
      status: form.status,
      notes: form.notes || null
    }
    const { error } = await supabase.from('wholesale_orders').insert(payload)
    if (error) { setFormError('Save failed: ' + error.message); setSaving(false); return }
    setSaving(false); setModal(false); load()
  }

  async function deleteOrder(o) {
    if (!window.confirm(`Delete wholesale order "${o.order_number}"? This also removes its line items and cannot be undone.`)) return
    await supabase.from('wholesale_orders').delete().eq('id', o.id)
    load()
  }

  let filtered = orders.filter(o =>
    o.order_number.toLowerCase().includes(search.toLowerCase()) ||
    (o.wholesale_customers?.name || '').toLowerCase().includes(search.toLowerCase())
  )
  if (filterStatus === 'active') filtered = filtered.filter(o => o.status !== 'closed' && o.status !== 'cancelled')
  else if (filterStatus === 'closed') filtered = filtered.filter(o => o.status === 'closed')
  else if (filterStatus === 'cancelled') filtered = filtered.filter(o => o.status === 'cancelled')

  const unitsFor = o => (o.wholesale_order_lines || []).reduce((s, l) => s + Number(l.quantity || 0), 0)

  return (
    <div className="page">
      <div className="page-header">
        <h1>Wholesale Orders</h1>
        {!tableError && (
          <div className="page-header-actions">
            <div className="search-input-wrap"><Search size={14} /><input className="input" placeholder="Search orders..." value={search} onChange={e => setSearch(e.target.value)} /></div>
            <select className="select" style={{ width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
              <option value="cancelled">Cancelled</option>
              <option value="all">All</option>
            </select>
            <button className="btn btn-primary" onClick={openNew}><Plus size={14} />New Order</button>
          </div>
        )}
      </div>

      {tableError && (
        <div className="card" style={{ borderColor: 'var(--warning)' }}>
          <div style={{ padding: '4px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <AlertTriangle size={16} style={{ color: 'var(--warning)' }} />
              <strong style={{ fontSize: '14px' }}>One-time setup required</strong>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '12px' }}>
              Run this SQL in your <strong>Supabase SQL editor</strong> to create the wholesale tables, then refresh:
            </p>
            <pre style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: '14px', borderRadius: 'var(--radius)', fontSize: '12px', lineHeight: 1.7, overflow: 'auto', margin: 0 }}>{SETUP_SQL}</pre>
          </div>
        </div>
      )}

      {!tableError && (
        <div className="card">
          {loading ? <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            : filtered.length === 0 ? <div className="empty-state"><Store size={40} /><p>No wholesale orders found</p></div>
            : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Order #</th><th>Buyer</th><th>Status</th><th>Ship Date</th><th>Lines</th><th>Units</th><th></th></tr></thead>
                  <tbody>
                    {filtered.map(o => (
                      <tr key={o.id}>
                        <td><Link to={`/wholesale-orders/${o.id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500, fontFamily: 'var(--mono)', fontSize: '13px' }}>{o.order_number}</Link></td>
                        <td>{o.wholesale_customers?.name || '—'}</td>
                        <td><span className={`badge badge-${STATUS_BADGE[o.status] || 'neutral'}`}>{o.status}</span></td>
                        <td>{o.ship_date ? format(new Date(o.ship_date + 'T12:00:00'), 'MMM d, yyyy') : '—'}</td>
                        <td>{(o.wholesale_order_lines || []).length}</td>
                        <td>{unitsFor(o) || '—'}</td>
                        <td><button className="btn btn-ghost btn-sm" title="Delete order" onClick={() => deleteOrder(o)}><Trash2 size={13} style={{ color: 'var(--danger)' }} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>New Wholesale Order</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {formError && <div className="alert alert-danger">{formError}</div>}
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Order Number *</label><input className="input" value={form.order_number} onChange={e => setForm(f => ({ ...f, order_number: e.target.value }))} placeholder="OC-2028202" /></div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    {['draft', 'confirmed', 'shipped', 'closed', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label className="form-label">Buyer</label>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setNewBuyer(b => !b); setForm(f => ({ ...f, customer_id: '' })) }}>{newBuyer ? 'Pick existing' : '+ New buyer'}</button>
                </div>
                {newBuyer ? (
                  <div className="form-grid">
                    <input className="input" value={buyerName} onChange={e => setBuyerName(e.target.value)} placeholder="Buyer name (e.g. SSENSE)" />
                    <input className="input" value={buyerEmail} onChange={e => setBuyerEmail(e.target.value)} placeholder="Contact email (optional)" />
                  </div>
                ) : (
                  <select className="select" value={form.customer_id} onChange={e => setForm(f => ({ ...f, customer_id: e.target.value }))}>
                    <option value="">— Select —</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                )}
              </div>
              <div className="form-group"><label className="form-label">Ship Date</label><input className="input" type="date" value={form.ship_date} onChange={e => setForm(f => ({ ...f, ship_date: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Notes</label><textarea className="textarea" rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.order_number}>{saving ? 'Creating...' : 'Create Order'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
